import { Box, Collapse, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AngleDownSmallIcon from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { StatusPill } from '@hapana/shared/web/components/shared/status-pill/status-pill.component'
import { EquipmentActionsMenu } from '_components/check-in-widget/equipment-actions-menu/equipment-actions-menu.component'
import { EquipmentImage } from '_components/check-in-widget/equipment-image/equipment-image.component'
import { getClientName, getTimeInfo } from '_components/check-in-widget/equipment-time.utils'
import { EQUIPMENT_STATUSES } from '_constants/equipments'

import { STATUS_CHIP_LABEL_KEYS } from '../manage-equipments.constants'
import { styles } from './equipment-row.styles'

const getClientInfo = (equipment, t) => {
  const { status } = equipment
  const clientName = getClientName(equipment)
  const hasClient = clientName !== '-'

  if (status === EQUIPMENT_STATUSES.IN_USE || status === EQUIPMENT_STATUSES.DELAY) {
    return hasClient ? clientName : t('checkIn:equipments.manageEquipments.test')
  }
  if (status === EQUIPMENT_STATUSES.DIRTY) {
    return hasClient ? clientName : t('checkIn:equipments.detail.noPreviousClient')
  }
  if (
    status === EQUIPMENT_STATUSES.DISABLED ||
    status === EQUIPMENT_STATUSES.ERROR ||
    status === EQUIPMENT_STATUSES.AVAILABLE
  ) {
    return hasClient ? clientName : null
  }
  return null
}

export const EquipmentRow = ({ equipment, siteId, diagnostics, now }) => {
  const { t } = useTranslation(['checkIn'])
  const [isExpanded, setIsExpanded] = useState(false)

  const clientInfo = getClientInfo(equipment, t)
  const timeInfo = getTimeInfo(equipment, now, t)
  const isExpandable = !!clientInfo || !!timeInfo
  const chipLabelKey = STATUS_CHIP_LABEL_KEYS[equipment.status]

  const handleToggle = () => {
    if (isExpandable) {
      setIsExpanded((prev) => !prev)
    }
  }

  return (
    <Box sx={styles.row}>
      <Box sx={[styles.header, isExpandable && styles.headerClickable]} onClick={handleToggle}>
        <Box sx={styles.headerInfo}>
          <EquipmentImage
            src={equipment.image?.medium || equipment.image?.thumbnail || equipment.image?.db}
            name={equipment.name}
            sx={styles.image}
          />
          <Box sx={styles.titleBlock}>
            <Typography typography="header4" color="gray.80" sx={styles.titleTruncate}>
              {equipment.name}
            </Typography>
            {equipment.manufacturer && (
              <Typography typography="small" color="gray.64" sx={styles.titleTruncate}>
                {equipment.manufacturer}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={styles.headerActions}>
          <StatusPill
            status={equipment.status}
            label={chipLabelKey ? t(chipLabelKey) : undefined}
          />
          <EquipmentActionsMenu equipment={equipment} siteId={siteId} diagnostics={diagnostics} />
          {isExpandable && (
            <Button
              variant={BUTTON_VARIANTS.ICON}
              icon={AngleDownSmallIcon}
              size={BUTTON_SIZE.SMALL}
              label="toggle-equipment-details"
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                handleToggle()
              }}
              sx={[styles.chevron, isExpanded && styles.chevronExpanded]}
            />
          )}
        </Box>
      </Box>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {clientInfo && (
          <Box sx={styles.detailRow}>
            <Box sx={styles.detailLabel}>
              <Typography typography="header4" color="gray.80">
                {t('checkIn:equipments.manageEquipments.client')}
              </Typography>
            </Box>
            <Box sx={styles.detailValue}>
              <Typography typography="medium" color="gray.80">
                {clientInfo}
              </Typography>
            </Box>
          </Box>
        )}
        {timeInfo && (
          <Box sx={styles.detailRow}>
            <Box sx={styles.detailLabel}>
              <Typography typography="header4" color="gray.80">
                {t('checkIn:equipments.manageEquipments.time')}
              </Typography>
            </Box>
            <Box sx={styles.detailValue}>
              <Typography typography="medium" color="gray.80">
                {timeInfo.primary}
              </Typography>
              {timeInfo.secondary && (
                <Typography typography="small" color="gray.56">
                  {timeInfo.secondary}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}

EquipmentRow.propTypes = {
  equipment: PropTypes.shape({
    equipmentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    manufacturer: PropTypes.string,
    timerId: PropTypes.number,
    lastInUseAt: PropTypes.string,
    lastDirtyAt: PropTypes.string,
    image: PropTypes.shape({
      medium: PropTypes.string,
      thumbnail: PropTypes.string,
      db: PropTypes.string,
    }),
    assignments: PropTypes.arrayOf(
      PropTypes.shape({
        equipmentAssignmentId: PropTypes.string,
        createdAt: PropTypes.string,
        doneAt: PropTypes.string,
        cancelledAt: PropTypes.string,
        timerMinutes: PropTypes.number,
        delayMinutes: PropTypes.number,
        client: PropTypes.shape({
          firstName: PropTypes.string,
          lastName: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
  siteId: PropTypes.string,
  diagnostics: PropTypes.shape({
    commPort: PropTypes.string,
  }),
  now: PropTypes.instanceOf(Date).isRequired,
}
